import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  alpha,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Fade,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/Preview";
import { CreateVariableBulkDto } from "../../../api/types/models/CreateVariableBulkDto";
import {
  validateFile,
  validateCsvContent,
  downloadSampleCsv,
  CsvValidationResult,
  CsvSeparator,
  CSV_SEPARATORS,
} from "@/utils/csvValidation";

interface ImportCsvFormProps {
  onSubmit: (data: { variables: CreateVariableBulkDto[] }) => Promise<{
    createdZones?: string[];
    existingZones?: string[];
    createdTables?: string[];
    existingTables?: string[];
    createdVariables?: string[];
    existingVariables?: string[];
    errors?: string[];
  } | void>;
  plantId: number;
  isPending: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function ImportCsvForm({
  onSubmit,
  plantId,
  isPending,
}: ImportCsvFormProps) {
    const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<CsvValidationResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [csvSeparator, setCsvSeparator] = useState<CsvSeparator>(CSV_SEPARATORS.SEMICOLON); // Default to semicolon for Spain
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<'idle' | 'importing' | 'success' | 'error'>('idle');
  const [importResults, setImportResults] = useState<{
    createdZones: string[];
    existingZones: string[];
    createdTables: string[];
    existingTables: string[];
    createdVariables: string[];
    existingVariables: string[];
    errors: string[];
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFileSelect = async (selectedFile: File) => {
    if (!selectedFile) return;

    // Validate file using utility function
    const fileValidation = validateFile(selectedFile);
    if (!fileValidation.isValid) {
      showSnackbar(fileValidation.error || "Error en el archivo", "error");
      return;
    }

    setFile(selectedFile);
    setProcessing(true);
    try {
      const text = await selectedFile.text();
      const result = validateCsvContent(text, csvSeparator);
      setValidationResult(result);

      if (result.isValid) {
        showSnackbar(
          `Archivo validado correctamente. ${result.stats.validRows} variables listas para importar.`,
          "success",
        );
      } else {
        const errorCount = result.errors.filter(
          (e) => e.severity === "error",
        ).length;
        showSnackbar(
          `Se encontraron ${errorCount} errores en el archivo.`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error reading file:", error);
      showSnackbar(
        "Error al leer el archivo. Asegúrate de que sea un CSV válido.",
        "error",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };
  const handleRemoveFile = () => {
    setFile(null);
    setValidationResult(null);
    setShowPreview(false);
    setImportStatus('idle');
    setImportProgress(0);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };const handleImport = async () => {
    if (!validationResult || !validationResult.isValid) return;
    
    try {
      setImportStatus('importing');
      setImportProgress(0);
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 20;
        });
      }, 200);
      
      showSnackbar(`Iniciando importación de ${validationResult.data.length} variables...`, 'info');
      
      // Call the import function and get detailed response
      const result = await onSubmit({ variables: validationResult.data });
      
      clearInterval(progressInterval);
      setImportProgress(100);
      
      // Wait a bit to show 100% progress
      setTimeout(() => {
        // Process the detailed API response
        const {
          createdZones = [],
          existingZones = [],
          createdTables = [],
          existingTables = [],
          createdVariables = [],
          existingVariables = [],
          errors = []
        } = result || {};
        
        const hasErrors = errors.length > 0;
        const hasCreations = createdVariables.length > 0 || createdZones.length > 0 || createdTables.length > 0;
        const hasExisting = existingVariables.length > 0 || existingZones.length > 0 || existingTables.length > 0;
        
        if (hasErrors && !hasCreations) {
          // Solo errores, no se creó nada
          setImportStatus('error');
          showSnackbar(
            `Error en la importación: ${errors.length} error(es) encontrado(s). No se crearon variables.`,
            'error'
          );
        } else if (hasCreations && !hasErrors) {
          // Éxito completo
          setImportStatus('success');
          showSnackbar(
            `¡Importación completada exitosamente! Se crearon ${createdVariables.length} variables${createdZones.length > 0 ? `, ${createdZones.length} zonas` : ''}${createdTables.length > 0 ? ` y ${createdTables.length} tablas` : ''}.`,
            'success'
          );
        } else if (hasCreations && hasErrors) {
          // Éxito parcial
          setImportStatus('success');
          showSnackbar(
            `Importación completada con advertencias. Se crearon ${createdVariables.length} variables, pero ${errors.length} tuvieron errores.`,
            'warning'
          );
        } else if (hasExisting && !hasErrors && !hasCreations) {
          // Todo ya existía
          setImportStatus('success');
          showSnackbar(
            `Importación completada. Todas las variables ya existían en el sistema.`,
            'info'
          );
        }
          // Store detailed results for showing in UI
        setImportResults({
          createdZones,
          existingZones,
          createdTables,
          existingTables,
          createdVariables,
          existingVariables,
          errors
        });
        
        // No automatic reset - user can choose when to start a new import
      }, 500);
      
    } catch (error) {
      setImportStatus('error');
      setImportProgress(0);
      setImportResults(null);
      showSnackbar(
        'Error durante la importación. Por favor, inténtalo de nuevo.', 
        'error'
      );
      console.error('Import error:', error);
    }
  };

  const getSeverityColor = (severity: "error" | "warning" | "success") => {
    switch (severity) {
      case "error":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
      case "success":
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            mb: 1,
          }}
        >
          Importar desde CSV
        </Typography>{" "}
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Sube un archivo CSV con las variables que deseas crear masivamente
        </Typography>
      </Box>
      {/* CSV Format Selector */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Formato CSV</InputLabel>
          <Select
            value={csvSeparator}
            onChange={(e) => setCsvSeparator(e.target.value as CsvSeparator)}
            label="Formato CSV"
            disabled={processing || isPending}
          >
            <MenuItem value={CSV_SEPARATORS.SEMICOLON}>
              Europeo (; punto y coma)
            </MenuItem>
            <MenuItem value={CSV_SEPARATORS.COMMA}>
              Internacional (, coma)
            </MenuItem>
          </Select>
          <FormHelperText>
            {csvSeparator === CSV_SEPARATORS.SEMICOLON
              ? "Formato usado en España y Europa (separador: ;)"
              : "Formato internacional estándar (separador: ,)"}
          </FormHelperText>
        </FormControl>
      </Box>
      {/* File Upload Area */}
      <Box
        sx={{
          p: 4,
          border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 2,
          bgcolor: dragActive
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.background.paper, 0.3),
          textAlign: "center",
          minHeight: 200,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          transition: "all 0.2s ease-in-out",
          cursor: "pointer",
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          style={{ display: "none" }}
          disabled={processing || isPending}
        />

        {processing ? (
          <>
            <CircularProgress size={48} />
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Validando archivo...
            </Typography>
          </>
        ) : file ? (
          <>
            <UploadFileIcon
              sx={{
                fontSize: 48,
                color: validationResult?.isValid
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {file.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
            <Button
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              color="error"
              size="small"
            >
              Remover archivo
            </Button>
          </>
        ) : (
          <>
            <FileUploadIcon
              sx={{
                fontSize: 48,
                color: theme.palette.text.secondary,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Arrastra un archivo CSV aquí o haz clic para seleccionar
            </Typography>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              disabled={processing || isPending}
            >
              Seleccionar archivo CSV
            </Button>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled }}
            >
              Máximo 5MB • Solo archivos .csv
            </Typography>
          </>
        )}
      </Box>
      {/* Validation Results */}
      {validationResult && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {validationResult.isValid ? (
              <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
            ) : (
              <ErrorIcon sx={{ color: theme.palette.error.main }} />
            )}
            Resultados de Validación
          </Typography>
          {/* Statistics */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Chip
              label={`${validationResult.stats.totalRows} filas totales`}
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${validationResult.stats.validRows} válidas`}
              color="success"
              variant={
                validationResult.stats.validRows > 0 ? "filled" : "outlined"
              }
              size="small"
            />
            {validationResult.stats.errorRows > 0 && (
              <Chip
                label={`${validationResult.stats.errorRows} con errores`}
                color="error"
                variant="filled"
                size="small"
              />
            )}{" "}
            {validationResult.stats.duplicateCombinations.length > 0 && (
              <Chip
                label={`${validationResult.stats.duplicateCombinations.length} duplicados`}
                color="warning"
                variant="filled"
                size="small"
              />
            )}
          </Box>
          {/* Warnings */}
          {validationResult.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Advertencias:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {validationResult.warnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}{" "}
          {/* Errors */}
          {validationResult.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Errores encontrados (
                {
                  validationResult.errors.filter((e) => e.severity === "error")
                    .length
                }
                ):
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                {validationResult.errors
                  .filter((e) => e.severity === "error")
                  .slice(0, 10)
                  .map((error, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Fila {error.row}:</strong> {error.error}
                      {error.value && ` (Valor: "${error.value}")`}
                    </Typography>
                  ))}
                {validationResult.errors.filter((e) => e.severity === "error")
                  .length > 10 && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", mt: 1 }}
                  >
                    ... y{" "}
                    {validationResult.errors.filter(
                      (e) => e.severity === "error",
                    ).length - 10}{" "}
                    errores más
                  </Typography>
                )}
              </Box>
            </Alert>
          )}
          {/* Warnings for non-critical errors */}
          {validationResult.errors.some((e) => e.severity === "warning") && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Advertencias (
                {
                  validationResult.errors.filter(
                    (e) => e.severity === "warning",
                  ).length
                }
                ):
              </Typography>
              <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                {validationResult.errors
                  .filter((e) => e.severity === "warning")
                  .slice(0, 5)
                  .map((warning, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Fila {warning.row}:</strong> {warning.error}
                    </Typography>
                  ))}
              </Box>
            </Alert>          )}
          
          {/* Import Progress */}
          {importStatus !== 'idle' && (
            <Fade in={true}>
              <Box sx={{ mt: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {importStatus === 'importing' && 'Importando variables...'}
                    {importStatus === 'success' && '¡Importación completada!'}
                    {importStatus === 'error' && 'Error en la importación'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(importProgress)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={importProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: importStatus === 'success' 
                        ? theme.palette.success.main 
                        : importStatus === 'error' 
                        ? theme.palette.error.main 
                        : theme.palette.primary.main
                    }
                  }}
                />
                {importStatus === 'success' && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    Las variables se han creado exitosamente y estarán disponibles en el sistema.
                  </Typography>
                )}
              </Box>
            </Fade>          )}

          {/* Detailed Import Results */}
          {importResults && importStatus === 'success' && (
            <Fade in={true}>
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Resumen Detallado de Importación
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Created Items */}
                  {(importResults.createdVariables.length > 0 || importResults.createdZones.length > 0 || importResults.createdTables.length > 0) && (
                    <Alert severity="success" sx={{ py: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                        ✅ Elementos Creados
                      </Typography>
                      {importResults.createdVariables.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Variables:</strong> {importResults.createdVariables.join(', ')}
                        </Typography>
                      )}
                      {importResults.createdZones.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Zonas:</strong> {importResults.createdZones.join(', ')}
                        </Typography>
                      )}
                      {importResults.createdTables.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Tablas:</strong> {importResults.createdTables.join(', ')}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {/* Existing Items */}
                  {(importResults.existingVariables.length > 0 || importResults.existingZones.length > 0 || importResults.existingTables.length > 0) && (
                    <Alert severity="info" sx={{ py: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'info.main' }}>
                        ℹ️ Elementos que ya Existían
                      </Typography>
                      {importResults.existingVariables.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Variables:</strong> {importResults.existingVariables.join(', ')}
                        </Typography>
                      )}
                      {importResults.existingZones.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Zonas:</strong> {importResults.existingZones.join(', ')}
                        </Typography>
                      )}
                      {importResults.existingTables.length > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Tablas:</strong> {importResults.existingTables.join(', ')}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {/* Errors */}
                  {importResults.errors.length > 0 && (
                    <Alert severity="error" sx={{ py: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>
                        ❌ Errores Encontrados
                      </Typography>
                      <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                        {importResults.errors.map((error, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            • {error}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Action Buttons */}<Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            {validationResult.isValid && (
              <>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={isPending || importStatus === 'importing'}
                  startIcon={
                    isPending || importStatus === 'importing' ? (
                      <CircularProgress size={16} />
                    ) : importStatus === 'success' ? (
                      <CheckCircleIcon />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  sx={{ minWidth: 140 }}
                >
                  {importStatus === 'importing'
                    ? "Importando..."
                    : importStatus === 'success'
                    ? "¡Completado!"
                    : isPending
                    ? "Importando..."
                    : `Importar ${validationResult.data.length} Variables`}
                </Button>{" "}                <Button
                  variant="outlined"
                  onClick={() => setShowPreview(true)}
                  disabled={isPending || importStatus === 'importing'}
                  startIcon={<PreviewIcon />}
                  sx={{ minWidth: 120 }}
                >
                  Vista Previa
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              onClick={handleRemoveFile}
              disabled={isPending || importStatus === 'importing'}
              color="error"
            >
              {importStatus === 'success' ? 'Nuevo Archivo' : 'Cancelar'}
            </Button>
          </Box>
        </Box>
      )}{" "}
      {/* CSV Format Example */}
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.text.secondary }}
          >
            Formato esperado del archivo CSV:
          </Typography>{" "}
          <Tooltip title="Descargar plantilla de ejemplo">
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => downloadSampleCsv(csvSeparator)}
              sx={{ ml: 2 }}
            >
              Descargar Plantilla
            </Button>
          </Tooltip>
        </Box>
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {" "}
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
            }}
          >
            {csvSeparator === CSV_SEPARATORS.SEMICOLON
              ? `variableName;scadaReferenceName;zoneName;tableName
Variable1;SCADA_REF_1;Zona A;Tabla 1
Variable2;SCADA_REF_2;Zona B;Tabla 2`
              : `variableName,scadaReferenceName,zoneName,tableName
Variable1,SCADA_REF_1,Zona A,Tabla 1
Variable2,SCADA_REF_2,Zona B,Tabla 2`}
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Requisitos del archivo:</strong>
          </Typography>{" "}
          <ul style={{ margin: "8px 0", paddingLeft: "1.2rem" }}>
            <li>
              Las 4 columnas son obligatorias: variableName, scadaReferenceName,
              zoneName, tableName
            </li>
            <li>
              Usar el separador seleccionado (
              {csvSeparator === CSV_SEPARATORS.SEMICOLON
                ? "punto y coma (;)"
                : "coma (,)"}
              )
            </li>
            <li>No debe haber filas vacías o con campos faltantes</li>
            <li>
              La combinación variable+zona+tabla debe ser única (se permite el
              mismo nombre de variable en diferentes zonas/tablas)
            </li>
            <li>Máximo 100 caracteres por campo</li>
            <li>Se permiten caracteres especiales como (), _, -, /, °, etc.</li>
            <li>Solo se restringen: &lt; &gt; : " / \ | ? *</li>
            <li>Codificación UTF-8 recomendada para caracteres especiales</li>
          </ul>
        </Alert>
      </Box>
      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Vista Previa de Variables a Importar
          {validationResult && (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {validationResult.data.length} variables válidas encontradas
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {validationResult && (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre Variable</TableCell>
                    <TableCell>Referencia SCADA</TableCell>
                    <TableCell>Zona</TableCell>
                    <TableCell>Tabla</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResult.data.map((variable, index) => (
                    <TableRow key={index}>
                      <TableCell>{variable.variableName}</TableCell>
                      <TableCell>{variable.scadaReferenceName}</TableCell>
                      <TableCell>{variable.zoneName}</TableCell>
                      <TableCell>{variable.tableName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowPreview(false);
              handleImport();
            }}
            disabled={isPending}
          >
            Confirmar Importación
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
